import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DISPLAY_HEADER_ICON_BACK,
  DISPLAY_PAGE_INFERENCE_FACTORS_ITEMS,
  PAGE_NAME_FEEDBACK,
} from "../../constants/common_constants";
import { InferenceFactorAccordionLists } from "../../features/components/inference_factors/ui_elements/InferenceFactorAccordionLists";
import { IInferenceFactorListType } from "../../features/components/inference_factors/types/inference_factor_type";
import { calcUtcDatetime } from "../../utils/basis_util";
import { GeneralCommonHeader } from "../common/GeneralCommonHeader";
import { useDtsAppContext } from "../provider/DtsAppContextProvider";
import { useErrorDialog, useInitUsageLog } from "../../hooks";
import { useTranslation } from "react-i18next";
import Feedback from "../../icons/Feedback.svg";

export const InferenceFactorItem = () => {
  // ページ遷移用
  const navigate = useNavigate();
  //言語切替用
  const { t } = useTranslation();

  // ローカルストレージから取得したデータを変換して格納する変数
  const [listData, setListData] = useState<IInferenceFactorListType>({
    inferenceFactor: [],
  });
  const [subjectName, setSubjectName] = useState<string>("");
  const [model, setModel] = useState<string>("");
  const [machineNumber, setMachineNumber] = useState<string>("");
  const { dacSubjectName, dacModel, dacMachineNumber, dacInferenceFactor } =
    useDtsAppContext();
  const userId =
    localStorage.getItem(
      `CognitoIdentityServiceProvider.${process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID}.LastAuthUser`
    ) || "";
  const displayPage = DISPLAY_PAGE_INFERENCE_FACTORS_ITEMS;
  const displayDateTime = calcUtcDatetime(); //現在日時(UTC時刻)

  // 利用ログ登録
  const { handleInit: initUsageLog } = useInitUsageLog({
    userId,
    displayPage,
    displayDateTime,
  });
  // エラーダイアログ
  const { showErrorDialog } = useErrorDialog();

  useEffect(() => {
    handleInferenceFactorItemlInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //ページ表示時に行うInferenceFactorItemの処理
  const handleInferenceFactorItemlInit = async () => {
    try {
      console.log("handleInferenceFactorItemlInit start.");

      // 初期状態としてコンテキストorローカルストレージからデータを取得
      getInitialInfo();
      //利用ログ登録
      const isError = await initUsageLog();
      if (isError) {
        return;
      }
    } catch (err) {
      console.log(`[${displayPage}]err: `, err);
      //getInitialInfo()で通信エラーになった場合
      if (
        err instanceof Error &&
        err.message === "inferenceFactor is unknown"
      ) {
        //要因取得失敗
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("推定要因項目画面.要因が見つかりません。"),
          "navigateToTopPage"
        ); //エラー", "要因が見つかりません。"(トップページに遷移する)
      } else if (
        err instanceof Error &&
        (err.message === "Expired token" || err.message === "Invalid token")
      ) {
        //LocalStorageのセッション情報クリア
        const userpool_client_id =
          process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID ?? "";
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.accessToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.refreshToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.idToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.clockDrift`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.LastAuthUser`
        );

        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.セッションがタイムアウトしました。"),
          "ydsyos"
        ); //エラー", "セッションがタイムアウトしました。(YDS/YOSに遷移する)
      } else {
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.通信エラーが発生しました。"),
          "navigateToTopPage"
        ); //エラー", "通信エラーが発生しました。(トップページに遷移する)
      }
    }
  };

  //Context or ローカルストレージからデータ取得
  const getInitialInfo = () => {
    try {
      //案件名、型式、機番、推定要因項目をContext or ローカルストレージから読み出す
      const _subjectName =
        dacSubjectName || localStorage.getItem("subjectName") || "";
      const _model = dacModel || localStorage.getItem("model") || "";
      const _machineNumber =
        dacMachineNumber || localStorage.getItem("machineNumber") || "";
      const infData =
        dacInferenceFactor || localStorage.getItem("inferenceFactor") || "";

      //案件名
      if (_subjectName) {
        setSubjectName(_subjectName);
      } else {
        console.log("subjectName is unknown");
      }

      //型式
      if (_model) {
        setModel(_model);
      } else {
        console.log("model is unknown");
      }

      //機番
      setMachineNumber(_machineNumber);

      //要因
      if (infData) {
        setListData(JSON.parse(infData));
      } else {
        //要因(Context、LocalStorageのどちらからも)取得失敗
        console.log("inferenceFactor is unknown");
        throw new Error("inferenceFactor is unknown");
      }

      //要因以外のパラメーター不足(Context、LocalStorageのどちらからも)取得失敗
      // 機番は読み込めなくても良い
      if (!_subjectName || !_model) {
        throw new Error("failed to load parameter.");
      }
    } catch (err) {
      throw err;
    }
  };

  // アコーディオンメニューをレンダリングする関数
  const renderInferenceFactorAccordion = () => {
    return listData.inferenceFactor.map((inferenceFactor) => (
      <InferenceFactorAccordionLists
        inferenceFactor={inferenceFactor}
        key={inferenceFactor.issueFactor.label}
      >
        <InferenceFactorAccordionLists inferenceFactor={inferenceFactor} />
      </InferenceFactorAccordionLists>
    ));
  };

  return (
    // スクロール不可に設定
    <div className="flex flex-col h-svh overflow-hidden">
      {/* header */}
      <GeneralCommonHeader
        title={t("推定要因項目画面.推定要因項目")} //推定要因項目
        displayheaderIcon={DISPLAY_HEADER_ICON_BACK}
        onBtnClick={() => {
          navigate(-1);
        }}
      />
      {/* header以下 */}
      <div className="flex flex-col m-4 h-full overflow-hidden">
        {/* 入力済項目 */}
        <div className="mb-2">
          <div className="pb-1 flex gap-2">
            <div className="shrink-0 max-w-[146px]">
              {t("推定要因項目画面.案件名")} :
            </div>
            <div className="flex-1 min-w-0 break-words">
              {subjectName}
              {/* 案件名： */}
            </div>
          </div>
          <div className="pb-1 flex gap-2">
            <div className="shrink-0">
              {t("推定要因項目画面.型式")} :{/* 型式： */}
            </div>
            <div className="flex-1 min-w-0 break-words">{model}</div>
          </div>
          <div className="pb-1 flex gap-2">
            <div className="shrink-0">
              {t("推定要因項目画面.機番")} :{/* 機番：  */}
            </div>
            <div className="flex-1 min-w-0 break-words">{machineNumber}</div>
          </div>
          {/* <p className="text-end pb-1">
            {t("推定要因項目画面.発生確率")}
          </p> */}
        </div>
        {/* フィードバック選択 */}
        <div className="h-full overflow-y-scroll overflow-x-hidden">
          {renderInferenceFactorAccordion()}
          {/* ボタン */}
          <div className="flex justify-center">
            <button
              className="flex justify-center items-center gap-1 border-2 border-main-red rounded-full bg-white w-[220px] py-2 overflow-hidden"
              onClick={() => navigate(`/${PAGE_NAME_FEEDBACK}`)}
            >
              <img
                src={Feedback}
                alt="Feedback"
                className="w-[1.75em] h-[1.75em]"
              />
              <span className="font-bold text-[20px]">
                {t("推定要因項目画面.フィードバック")}
                {/* フィードバック */}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
